import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories, mainSiteUrl } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 60);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  const toggleMobileMenu = () => {
    setMobileMenuOpen((prev) => !prev);
  };

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
  };

  const navLinkClass = scrolled
    ? 'text-sm text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap'
    : 'text-sm text-background-50 hover:text-background-100 transition-colors whitespace-nowrap';

  const buyBtnClass = scrolled
    ? 'text-sm px-5 py-2 border-2 border-primary-500 text-primary-600 rounded-full hover:bg-primary-500 hover:text-background-50 transition-all whitespace-nowrap cursor-pointer font-label font-medium'
    : 'text-sm px-5 py-2 border-2 border-background-50/70 text-background-50 rounded-full hover:bg-background-50 hover:text-foreground-950 transition-all whitespace-nowrap cursor-pointer font-label font-medium';

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-[60] transition-all duration-300 ${
          scrolled
            ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
            : 'bg-foreground-900/30 backdrop-blur-sm'
        }`}
      >
        <div className="flex items-center justify-between px-4 md:px-8 py-3.5">
          <Link
            to="/"
            className={`text-lg md:text-xl font-heading font-bold whitespace-nowrap transition-colors ${
              scrolled ? 'text-foreground-950' : 'text-background-50'
            }`}
          >
            LitBuy Spreadsheet Hub
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className={navLinkClass}>
              Home
            </Link>

            <Link to="/shopping-guide" className={navLinkClass}>
              Shopping Guide
            </Link>

            <div ref={dropdownRef} className="relative">
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-1 text-sm transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled ? 'text-foreground-700 hover:text-primary-600' : 'text-background-50 hover:text-background-100'
                }`}
              >
                Categories
                <span className={`w-4 h-4 flex items-center justify-center transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}>
                  <i className="ri-arrow-down-s-line text-xs"></i>
                </span>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full right-0 mt-2 w-64 bg-background-50 rounded-xl shadow-lg border border-background-200/70 py-2 max-h-80 overflow-y-auto z-[70]">
                  {categories.map((cat) => (
                    <a
                      key={cat.id}
                      href={cat.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="flex items-center gap-2.5 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors whitespace-nowrap"
                      onClick={() => setDropdownOpen(false)}
                    >
                      <span className="w-5 h-5 flex items-center justify-center bg-accent-100 rounded-full flex-shrink-0">
                        <i className={`${cat.icon} text-[10px] text-accent-600`}></i>
                      </span>
                      {cat.name}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to="/what-is-litbuy-spreadsheet" className={navLinkClass}>
              Beginner Guide
            </Link>

            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`${buyBtnClass} ml-2`}
            >
              buy it now
            </a>
          </div>

          {/* Mobile: Buy button + Menu toggle */}
          <div className="md:hidden flex items-center gap-3">
            <a
              href={mainSiteUrl}
              target="_blank"
              rel="noopener nofollow"
              className={`text-xs px-3 py-1.5 rounded-full whitespace-nowrap cursor-pointer font-label font-medium transition-all ${
                scrolled
                  ? 'border border-primary-500 text-primary-600 hover:bg-primary-500 hover:text-background-50'
                  : 'border border-background-50/70 text-background-50 hover:bg-background-50 hover:text-foreground-950'
              }`}
            >
              buy it now
            </a>
            <button
              onClick={toggleMobileMenu}
              className={`w-9 h-9 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
                scrolled ? 'text-foreground-800 hover:bg-background-100' : 'text-background-50 hover:bg-background-50/20'
              }`}
              aria-label="Toggle menu"
              aria-expanded={mobileMenuOpen}
            >
              <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay Backdrop */}
      <div
        className={`md:hidden fixed inset-0 z-[50] bg-foreground-950/40 backdrop-blur-sm transition-opacity duration-300 ${
          mobileMenuOpen ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        onClick={closeMobileMenu}
      />

      {/* Mobile Menu Panel */}
      <div
        className={`md:hidden fixed left-0 right-0 z-[55] bg-background-50 shadow-xl overflow-y-auto transition-all duration-300 ease-out ${
          mobileMenuOpen
            ? 'top-[57px] bottom-0 opacity-100 translate-y-0'
            : 'top-[57px] bottom-0 opacity-0 -translate-y-4 pointer-events-none'
        }`}
        style={
          mobileMenuOpen
            ? { maxHeight: 'calc(100vh - 57px)' }
            : { maxHeight: 'calc(100vh - 57px)' }
        }
      >
        <div className="px-4 py-5 space-y-1">
          {/* Main Nav Links */}
          <Link
            to="/"
            onClick={closeMobileMenu}
            className="flex items-center gap-3 px-4 py-3 text-base font-label font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center text-primary-600">
              <i className="ri-home-line"></i>
            </span>
            Home
          </Link>

          <Link
            to="/shopping-guide"
            onClick={closeMobileMenu}
            className="flex items-center gap-3 px-4 py-3 text-base font-label font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center text-primary-600">
              <i className="ri-shopping-cart-line"></i>
            </span>
            Shopping Guide
          </Link>

          <Link
            to="/what-is-litbuy-spreadsheet"
            onClick={closeMobileMenu}
            className="flex items-center gap-3 px-4 py-3 text-base font-label font-semibold text-foreground-900 hover:bg-background-100 rounded-lg transition-colors"
          >
            <span className="w-5 h-5 flex items-center justify-center text-primary-600">
              <i className="ri-book-open-line"></i>
            </span>
            Beginner Guide
          </Link>

          {/* Divider */}
          <div className="border-t border-background-200/70 my-3"></div>

          {/* Categories Section */}
          <p className="px-4 py-2 text-xs font-label uppercase tracking-wider text-foreground-500">
            Categories
          </p>

          <div className="grid grid-cols-2 gap-1 px-1">
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                onClick={closeMobileMenu}
                className="flex items-center gap-2 px-3 py-2.5 text-sm text-foreground-700 hover:bg-background-100 rounded-lg transition-colors"
              >
                <span className="w-6 h-6 flex items-center justify-center bg-accent-100 rounded-full flex-shrink-0">
                  <i className={`${cat.icon} text-[10px] text-accent-600`}></i>
                </span>
                <span className="text-xs leading-tight">{cat.name}</span>
              </a>
            ))}
          </div>

          {/* Divider */}
          <div className="border-t border-background-200/70 my-3"></div>

          {/* Mobile CTA */}
          <a
            href={mainSiteUrl}
            target="_blank"
            rel="noopener nofollow"
            onClick={closeMobileMenu}
            className="flex items-center justify-center gap-2 mx-1 py-3 bg-primary-500 text-background-50 rounded-full font-label font-semibold text-sm hover:bg-primary-600 transition-all"
          >
            Visit Kaspreadsheet Store
            <span className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-up-line"></i>
            </span>
          </a>

          {/* Footer in menu */}
          <div className="pt-4 pb-2 text-center">
            <p className="text-[10px] text-foreground-500 font-label">
              LitBuy Spreadsheet Hub &middot; All rights reserved
            </p>
          </div>
        </div>
      </div>
    </>
  );
}